/**
 * A rolagem de cada perícia — <b>num lugar só</b>.
 *
 * <p>Ela nasceu duas vezes: na Tier List, para as seis de coleta e criação, e no Combate,
 * para as onze da luta. Eram dois rascunhos, em duas chaves, com o mesmo formato — e
 * régua duplicada diverge, é só questão de quando. Agora as dezoito moram aqui, e quem
 * edita numa tela vê a mudança na outra.
 *
 * <p>Quem <b>escreve</b> é a Personagem: é lá que os atributos são definidos, e a rolagem
 * é a conta que os usa. A Tier List continua editando as suas seis porque é onde o time
 * fecha os números da coleta — mas é o mesmo dado, não uma cópia.
 */
import { SIGLA_APOSENTADA } from './atributos';
import { TODAS_AS_PERICIAS, equacaoEmBranco } from './pericias';
import type { Equacao, Pericia } from './pericias';

export const CHAVE_ROLAGENS = 'gg-pilot:rolagens:v1';

/** As chaves de onde as rolagens vinham antes. Lidas uma vez, para semear a nova. */
const CHAVE_ANTIGA_COMBATE = 'gg-pilot:combate:equacoes';
const CHAVE_ANTIGA_TIER = 'gg-pilot:areas-e-tier:regras:v2';

/** A equação de cada perícia, por id. Toda perícia tem entrada, mesmo em branco. */
export type Rolagens = Record<string, Equacao>;

export const rolagensEmBranco = (): Rolagens =>
  Object.fromEntries(TODAS_AS_PERICIAS.map((p) => [p.id, equacaoEmBranco()]));

/**
 * Encaixa o que foi lido sobre o padrão, campo a campo.
 *
 * <p>Reconstruir a partir do branco é o que faz uma perícia nova não quebrar o rascunho
 * e uma perícia que saiu da lista não ficar assombrando o que se lê. O mesmo cuidado
 * que a tela de Combate já tinha — só que agora vale para as dezoito.
 */
function encaixar(base: Rolagens, lido: unknown): Rolagens {
  if (!lido || typeof lido !== 'object') return base;
  const fonte = lido as Record<string, Partial<Equacao> | undefined>;
  for (const id of Object.keys(base)) {
    const dele = fonte[id];
    if (dele) base[id] = { ...base[id], ...dele, ...siglasAtuais(dele) };
  }
  return base;
}

/**
 * Troca a sigla aposentada pela que a substituiu.
 *
 * <p>`SAB` virou `INT` quando a ficha do alpha mostrou o nome que o jogo usa. A sigla é o
 * que fica gravado na rolagem, então sem esta troca a fórmula montada apontaria para um
 * atributo que não existe mais — e o `select` renderiza valor desconhecido como <b>campo
 * vazio</b>, que é a pior falha possível aqui: parece que ninguém escreveu, quando
 * escreveram e o dado ainda está lá.
 */
function siglasAtuais(e: Partial<Equacao>): Partial<Equacao> {
  const trocar = (s?: string) => (s && SIGLA_APOSENTADA[s]) || s;
  const troca: Partial<Equacao> = {};
  if (e.atributoA) troca.atributoA = trocar(e.atributoA);
  if (e.atributoB) troca.atributoB = trocar(e.atributoB);
  return troca;
}

/**
 * As rolagens a partir do que estava gravado na página `rolagens`.
 *
 * <p>É por aqui que as telas leem hoje. O encaixe-sobre-o-branco é o que faz uma perícia
 * nova não quebrar o que já foi escrito, e uma que saiu da lista não ficar assombrando.
 */
export const interpretarRolagens = (guardado: unknown): Rolagens =>
  encaixar(rolagensEmBranco(), guardado);

/**
 * O rascunho que ficou no `localStorage`, para a migração de uma vez só.
 *
 * <p><b>Não é mais a fonte</b> — as rolagens moram no GG Pilot desde que as telas de
 * Game Design passaram a gravar no banco. Esta função sobrevive como <i>ponte</i>: na
 * primeira abertura de quem já tinha rascunho no navegador, é ela que o entrega ao
 * servidor. Ver `useRascunho`.
 *
 * <p>Semeia também das duas chaves anteriores. A Tier List já trocou de chave uma vez
 * <i>sem</i> migrar, e estava certa: lá o formato tinha mudado, e carregar o velho no
 * novo daria uma equação vazia que parecia preenchida. Aqui é o contrário — o formato de
 * `Equacao` é idêntico nos três lugares —, então jogar fora o que o time já digitou
 * seria perda gratuita.
 */
export function lerRolagens(): Rolagens {
  const base = rolagensEmBranco();
  try {
    const cru = localStorage.getItem(CHAVE_ROLAGENS);
    if (cru) return encaixar(base, JSON.parse(cru));

    const combate = localStorage.getItem(CHAVE_ANTIGA_COMBATE);
    if (combate) encaixar(base, JSON.parse(combate));

    const tier = localStorage.getItem(CHAVE_ANTIGA_TIER);
    if (tier) {
      const lido = JSON.parse(tier) as Record<string, { equacoes?: unknown } | undefined>;
      encaixar(base, lido.coleta?.equacoes);
      encaixar(base, lido.craft?.equacoes);
    }
    return base;
  } catch {
    return base;
  }
}

/**
 * O rascunho do navegador, ou nada, se nunca houve um.
 *
 * <p>A diferença importa: `lerRolagens` sempre devolve as dezoito, em branco quando não
 * há o que ler. Semear com isso gravaria dezoito equações vazias e carimbaria a página
 * como escrita — e daí em diante nenhuma migração aconteceria, porque a página deixaria
 * de parecer nova. Por isso a pergunta aqui é sobre as <b>chaves</b>, não sobre o
 * conteúdo delas.
 */
export function rolagensDoNavegador(): Rolagens | undefined {
  const alguma = [CHAVE_ROLAGENS, CHAVE_ANTIGA_COMBATE, CHAVE_ANTIGA_TIER].some((k) => {
    try {
      return localStorage.getItem(k) != null;
    } catch {
      return false;
    }
  });
  return alguma ? lerRolagens() : undefined;
}

/**
 * Quantas rolagens de uma lista já estão montadas.
 *
 * <p>Montada é ter os <b>dois atributos</b>: o dado já nasce em `d20`, então contá-lo
 * faria dezoito rolagens em branco aparecerem como dezoito prontas.
 *
 * <p>As que só entram depois da Season 1 ficam fora da conta — elas recusam entrada, e
 * um denominador que inclui o que não se pode preencher nunca fecha.
 */
export function montadas(rolagens: Rolagens, pericias: Pericia[]) {
  const contam = pericias.filter((p) => !p.depoisDaSeason1);
  const prontas = contam.filter((p) => {
    const e = rolagens[p.id];
    return Boolean(e?.atributoA && e?.atributoB);
  });
  return { prontas: prontas.length, total: contam.length };
}
